import React, { ForwardedRef } from 'react';
import {
  Dialog as MuiDialog,
  DialogClasses as MuiDialogClasses,
  DialogProps as MuiDialogProps,
} from '@mui/material';

import Typography from '../Typography/index.js';

export interface DialogProps extends Pick<MuiDialogProps, 'open' | 'onClose'> {
  /**
   * Content for the action button area of the modal.
   */
  children: React.ReactNode;

  /**
   * @ignore
   * Classes applied to the MuiDialog component.
   */
  className?: string;

  /**
   * Some text to provide more information about what action the user should take.
   */
  description?: React.ReactNode;

  /**
   * An optional icon to display at the top of the dialog.
   */
  Icon?: React.ReactNode;

  /**
   * The title of the Dialog
   */
  title?: React.ReactNode;
}

const classes: Partial<MuiDialogClasses> = {
  paper: 'ds-dialog__paper',
};

/**
 * We have a desktop and mobile variant for dialog modals, with 3 different fixed sizes for desktop modals.
 * All dialog modals are positioned center with a 40% overlay behind it.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-library?node-id=4349%3A4971&mode=dev
 * @see https://mui.com/material-ui/react-dialog/
 */
const Dialog = React.forwardRef(
  (
    {
      children,
      className,
      description,
      Icon,
      open,
      title,
      onClose,
    }: DialogProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <MuiDialog
        classes={classes}
        className={className}
        fullWidth
        open={open}
        ref={ref}
        onClose={onClose}
      >
        <>
          {Icon && <div className="ds-dialog__icon-wrapper">{Icon}</div>}
          <div className="ds-dialog__header">
            {title && (
              <Typography variant="title-large" bold>
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="paragraph-medium">{description}</Typography>
            )}
          </div>
          <div className="ds-dialog__actions">{children}</div>
        </>
      </MuiDialog>
    );
  },
);

Dialog.displayName = 'Dialog';

export default Dialog;
